import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { RoundedBox } from '@react-three/drei';
import * as THREE from 'three';

const THEMES = {
  dark: {
    robotBody: '#1e1e1e',
    robotFace: '#252525',
    robotSocket: '#000000ff',
    robotEye: '#ffffff',
    robotEyeEmissive: '#ffffff',
    robotMouth: '#ffffffff',
    robotEar: '#282828',
    robotNeck: '#1a1a1a',
    robotChest: '#181818',
    robotAntenna: '#333333',
    robotAntennaTip: '#d0d0d0',
    robotAntennaTipEmissive: '#ffffff',
    robotChestLight: '#666666',
    robotChestEmissive: '#aaaaaa',
    particle: '#ffffff',
    particleSize: 0.05,
    particleOpacity: 0.8,
    wireframe: '#ffffff',
    wireframeOpacity: 0.34,
    wireframeLinewidth: 2,
    grid: ['#ffffff', '#ffffff'],
    f22: '#ffffff',
    f22Opacity: 0.7,
    f22Linewidth: 2,
    ambient: 0.35,
    directional: 0.7,
    point1: '#cccccc',
    point2: '#888888',
  },
  light: {
    robotBody: '#8a8a8a',
    robotFace: '#7a7a7a',
    robotSocket: '#444444',
    robotEye: '#ffffffff',
    robotEyeEmissive: '#333333',
    robotMouth: '#fefefeff',
    robotEar: '#777777',
    robotNeck: '#666666',
    robotChest: '#6a6a6a',
    robotAntenna: '#aaaaaa',
    robotAntennaTip: '#444444',
    robotAntennaTipEmissive: '#555555',
    robotChestLight: '#999999',
    robotChestEmissive: '#555555',
    particle: '#999999',
    particleSize: 0.035,
    particleOpacity: 0.7,
    wireframe: '#bbbbbb',
    wireframeOpacity: 0.2,
    wireframeLinewidth: 1,
    grid: ['#c0c0c0', '#cccccc'],
    f22: '#aaaaaa',
    f22Opacity: 0.4,
    f22Linewidth: 1,
    ambient: 0.6,
    directional: 0.8,
    point1: '#666666',
    point2: '#888888',
  },
};

function RobotBuddy({ mouse, t }) {
  const groupRef = useRef();
  const headRef = useRef();
  const leftEyeRef = useRef();
  const rightEyeRef = useRef();
  const antennaGroupRef = useRef();
  const antennaTipRef = useRef();
  const chestLightRef = useRef();
  const mouthRef = useRef();

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    const mx = mouse.current.x;
    const my = mouse.current.y;

    if (groupRef.current) {
      groupRef.current.position.y = -0.1 + Math.sin(time * 0.8) * 0.08;
      groupRef.current.rotation.z = Math.sin(time * 0.5) * 0.015;
    }

    if (headRef.current) {
      const targetRotY = mx * 0.6;
      const targetRotX = -my * 0.35;
      headRef.current.rotation.y += (targetRotY - headRef.current.rotation.y) * 0.12;
      headRef.current.rotation.x += (targetRotX - headRef.current.rotation.x) * 0.12;
    }

    const eyeOffsetX = mx * 0.07;
    const eyeOffsetY = my * 0.05;

    if (leftEyeRef.current) {
      leftEyeRef.current.position.x = -0.28 + eyeOffsetX;
      leftEyeRef.current.position.y = 0.1 + eyeOffsetY;
    }
    if (rightEyeRef.current) {
      rightEyeRef.current.position.x = 0.28 + eyeOffsetX;
      rightEyeRef.current.position.y = 0.1 + eyeOffsetY;
    }

    const blinkCycle = time % 4;
    const isBlinking = blinkCycle > 3.7;
    const blinkScale = isBlinking ? 0.1 : 1;
    if (leftEyeRef.current) {
      leftEyeRef.current.scale.y = THREE.MathUtils.lerp(
        leftEyeRef.current.scale.y, blinkScale, 0.3
      );
    }
    if (rightEyeRef.current) {
      rightEyeRef.current.scale.y = THREE.MathUtils.lerp(
        rightEyeRef.current.scale.y, blinkScale, 0.3
      );
    }

    if (antennaGroupRef.current) {
      antennaGroupRef.current.rotation.z = Math.sin(time * 1.5) * 0.08 + mx * 0.08;
    }

    if (antennaTipRef.current) {
      antennaTipRef.current.emissiveIntensity = 0.4 + Math.sin(time * 2.5) * 0.2;
    }

    if (chestLightRef.current) {
      chestLightRef.current.emissiveIntensity = 0.3 + Math.sin(time * 1.8) * 0.15;
    }

    if (mouthRef.current) {
      mouthRef.current.scale.x = 1 + Math.sin(time * 1.2) * 0.08;
    }
  });

  return (
    <group ref={groupRef} position={[1.5, -0.1, 0]} scale={[1.2, 1.2, 1.2]}>
      <group ref={headRef}>
        <RoundedBox args={[1, 0.85, 0.75]} radius={0.12} smoothness={4}>
          <meshStandardMaterial color={t.robotBody} metalness={0.4} roughness={0.6} />
        </RoundedBox>

        <RoundedBox args={[0.85, 0.65, 0.08]} radius={0.06} position={[0, 0, 0.36]}>
          <meshStandardMaterial color={t.robotFace} metalness={0.3} roughness={0.7} />
        </RoundedBox>

        <mesh position={[-0.28, 0.1, 0.41]}>
          <circleGeometry args={[0.12, 32]} />
          <meshStandardMaterial color={t.robotSocket} />
        </mesh>
        <mesh position={[0.28, 0.1, 0.41]}>
          <circleGeometry args={[0.12, 32]} />
          <meshStandardMaterial color={t.robotSocket} />
        </mesh>

        <mesh ref={leftEyeRef} position={[-0.28, 0.1, 0.42]}>
          <circleGeometry args={[0.065, 32]} />
          <meshStandardMaterial
            color={t.robotEye}
            emissive={t.robotEyeEmissive}
            emissiveIntensity={0.7}
          />
        </mesh>
        <mesh ref={rightEyeRef} position={[0.28, 0.1, 0.42]}>
          <circleGeometry args={[0.065, 32]} />
          <meshStandardMaterial
            color={t.robotEye}
            emissive={t.robotEyeEmissive}
            emissiveIntensity={0.7}
          />
        </mesh>

        <mesh ref={mouthRef} position={[0, -0.15, 0.41]}>
          <planeGeometry args={[0.22, 0.035]} />
          <meshStandardMaterial color={t.robotMouth} />
        </mesh>

        <group ref={antennaGroupRef} position={[0, 0.43, 0]}>
          <mesh position={[0, 0.13, 0]}>
            <cylinderGeometry args={[0.012, 0.018, 0.26]} />
            <meshStandardMaterial color={t.robotAntenna} metalness={0.8} roughness={0.2} />
          </mesh>
          <mesh position={[0, 0.3, 0]}>
            <sphereGeometry args={[0.04, 16, 16]} />
            <meshStandardMaterial
              ref={antennaTipRef}
              color={t.robotAntennaTip}
              emissive={t.robotAntennaTipEmissive}
              emissiveIntensity={0.4}
            />
          </mesh>
        </group>

        <mesh position={[-0.52, 0, 0]}>
          <boxGeometry args={[0.04, 0.18, 0.14]} />
          <meshStandardMaterial color={t.robotEar} metalness={0.5} roughness={0.5} />
        </mesh>
        <mesh position={[0.52, 0, 0]}>
          <boxGeometry args={[0.04, 0.18, 0.14]} />
          <meshStandardMaterial color={t.robotEar} metalness={0.5} roughness={0.5} />
        </mesh>
      </group>

      <mesh position={[0, -0.55, 0]}>
        <cylinderGeometry args={[0.1, 0.14, 0.18]} />
        <meshStandardMaterial color={t.robotNeck} metalness={0.5} roughness={0.5} />
      </mesh>

      <RoundedBox args={[0.65, 0.4, 0.45]} radius={0.07} position={[0, -0.82, 0]}>
        <meshStandardMaterial color={t.robotChest} metalness={0.3} roughness={0.7} />
      </RoundedBox>

      <mesh position={[0, -0.78, 0.24]}>
        <circleGeometry args={[0.04, 16]} />
        <meshStandardMaterial
          ref={chestLightRef}
          color={t.robotChestLight}
          emissive={t.robotChestEmissive}
          emissiveIntensity={0.3}
        />
      </mesh>
    </group>
  );
}

function createF22Geometry() {
  const v = [
    0, 0, -3,                 // 0: nose tip
    -0.2, 0.1, -2.2,          // 1
    0.2, 0.1, -2.2,           // 2
    0.2, -0.06, -2.2,         // 3
    -0.2, -0.06, -2.2,        // 4
    -0.3, 0.22, -1.3,         // 5: cockpit
    0.3, 0.22, -1.3,          // 6
    0.32, -0.08, -1.3,        // 7
    -0.32, -0.08, -1.3,       // 8
    -0.44, 0.14, -0.2,        // 9: intake
    0.44, 0.14, -0.2,         // 10
    0.44, -0.14, -0.2,        // 11
    -0.44, -0.14, -0.2,       // 12
    -0.44, 0.12, 1.0,         // 13: mid body
    0.44, 0.12, 1.0,          // 14
    0.44, -0.12, 1.0,         // 15
    -0.44, -0.12, 1.0,        // 16
    -0.34, 0.09, 2.3,         // 17: rear
    0.34, 0.09, 2.3,          // 18
    0.34, -0.09, 2.3,         // 19
    -0.34, -0.09, 2.3,        // 20
    -0.2, 0.05, 3.0,          // 21: exhaust
    0.2, 0.05, 3.0,           // 22
    0.2, -0.05, 3.0,          // 23
    -0.2, -0.05, 3.0,         // 24
    -2.2, 0, 0.3,             // 25: left wing tip
    2.2, 0, 0.3,              // 26: right wing tip
    -1.5, 0, 1.5,             // 27: left wing trail
    1.5, 0, 1.5,              // 28: right wing trail
    -0.48, 0.65, 2.2,         // 29: left vtail tip
    0.48, 0.65, 2.2,          // 30: right vtail tip
    -1.1, 0, 2.7,             // 31: left htail tip
    1.1, 0, 2.7,              // 32: right htail tip
  ];

  const edges = [
    0,1, 0,2, 0,3, 0,4,
    1,2, 2,3, 3,4, 4,1,
    1,5, 2,6, 3,7, 4,8,
    5,6, 6,7, 7,8, 8,5,
    5,9, 6,10, 7,11, 8,12,
    9,10, 10,11, 11,12, 12,9,
    9,13, 10,14, 11,15, 12,16,
    13,14, 14,15, 15,16, 16,13,
    13,17, 14,18, 15,19, 16,20,
    17,18, 18,19, 19,20, 20,17,
    17,21, 18,22, 19,23, 20,24,
    21,22, 22,23, 23,24, 24,21,
    9,25, 12,25, 25,27, 27,16,
    10,26, 11,26, 26,28, 28,15,
    13,29, 29,17,
    14,30, 30,18,
    20,31, 31,17,
    19,32, 32,18,
  ];

  const positions = new Float32Array((edges.length / 2) * 6);
  for (let i = 0; i < edges.length; i += 2) {
    const a = edges[i];
    const b = edges[i + 1];
    const idx = (i / 2) * 6;
    positions[idx]     = v[a * 3];
    positions[idx + 1] = v[a * 3 + 1];
    positions[idx + 2] = v[a * 3 + 2];
    positions[idx + 3] = v[b * 3];
    positions[idx + 4] = v[b * 3 + 1];
    positions[idx + 5] = v[b * 3 + 2];
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  return geom;
}

function F22Wireframe({ t }) {
  const ref = useRef();
  const geometry = useMemo(() => createF22Geometry(), []);

  useFrame((state) => {
    if (!ref.current) return;
    const time = state.clock.elapsedTime * 0.12;

    const x = Math.sin(time) * 5;
    const z = Math.cos(time) * 3 - 3;
    const y = Math.sin(time * 0.7) * 1.2 + 0.5;

    const dt = 0.01;
    const nx = Math.sin(time + dt) * 5;
    const nz = Math.cos(time + dt) * 3 - 3;

    const heading = Math.atan2(x - nx, z - nz);
    const bank = -Math.cos(time) * 0.35;
    const pitch = -Math.cos(time * 0.7) * 0.08;

    ref.current.position.set(x, y, z);
    ref.current.rotation.order = 'YXZ';
    ref.current.rotation.y = heading;
    ref.current.rotation.x = pitch;
    ref.current.rotation.z = bank;
  });

  return (
    <lineSegments ref={ref} geometry={geometry} scale={0.55}>
      <lineBasicMaterial
        color={t.f22}
        transparent
        opacity={t.f22Opacity}
        linewidth={t.f22Linewidth}
      />
    </lineSegments>
  );
}

function Particles({ count = 180, mouse, t }) {
  const meshRef = useRef();
  const velocities = useRef([]);

  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    velocities.current = [];
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 25;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 15;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 15 - 3;
      velocities.current.push({
        x: (Math.random() - 0.5) * 0.003,
        y: (Math.random() - 0.5) * 0.002,
        z: (Math.random() - 0.5) * 0.001,
      });
    }
    return pos;
  }, [count]);

  const geometry = useMemo(() => {
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geom;
  }, [positions]);

  useFrame(() => {
    if (!meshRef.current) return;
    const posAttr = meshRef.current.geometry.attributes.position;
    const arr = posAttr.array;
    const mx = mouse.current.x * 6;
    const my = mouse.current.y * 4;

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const dx = arr[i3] - mx;
      const dy = arr[i3 + 1] - my;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 2.5) {
        const force = (2.5 - dist) * 0.02;
        arr[i3] += dx * force;
        arr[i3 + 1] += dy * force;
      }

      arr[i3] += velocities.current[i].x;
      arr[i3 + 1] += velocities.current[i].y;
      arr[i3 + 2] += velocities.current[i].z;

      if (arr[i3] > 12.5) arr[i3] = -12.5;
      if (arr[i3] < -12.5) arr[i3] = 12.5;
      if (arr[i3 + 1] > 7.5) arr[i3 + 1] = -7.5;
      if (arr[i3 + 1] < -7.5) arr[i3 + 1] = 7.5;
      if (arr[i3 + 2] > 5) arr[i3 + 2] = -10;
      if (arr[i3 + 2] < -10) arr[i3 + 2] = 5;
    }

    posAttr.needsUpdate = true;
  });

  return (
    <points ref={meshRef} geometry={geometry}>
      <pointsMaterial
        size={t.particleSize}
        color={t.particle}
        transparent
        opacity={t.particleOpacity}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

function FloatingShape({ position, type, speed = 0.3, size = 0.4, t }) {
  const ref = useRef();

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    if (!ref.current) return;
    ref.current.rotation.x = time * speed * 0.5;
    ref.current.rotation.y = time * speed * 0.7;
    ref.current.rotation.z = time * speed * 0.3;
    ref.current.position.y = position[1] + Math.sin(time * speed + position[0]) * 0.4;
  });

  const geometryEl = (() => {
    switch (type) {
      case 'octahedron': return <octahedronGeometry args={[size]} />;
      case 'tetrahedron': return <tetrahedronGeometry args={[size]} />;
      case 'icosahedron': return <icosahedronGeometry args={[size, 0]} />;
      default: return <boxGeometry args={[size, size, size]} />;
    }
  })();

  return (
    <mesh ref={ref} position={position}>
      {geometryEl}
      <meshStandardMaterial
        color={t.wireframe}
        wireframe
        transparent
        opacity={t.wireframeOpacity}
        wireframeLinewidth={t.wireframeLinewidth}
      />
    </mesh>
  );
}

function GridFloor({ t }) {
  return (
    <gridHelper
      args={[40, 40, t.grid[0], t.grid[1]]}
      position={[0, -2.5, 0]}
    />
  );
}

function CameraRig({ mouse }) {
  useFrame((state) => {
    const mx = mouse.current.x;
    const my = mouse.current.y;
    state.camera.position.x += (mx * 0.3 - state.camera.position.x) * 0.02;
    state.camera.position.y += (my * 0.15 - state.camera.position.y) * 0.02;
    state.camera.lookAt(0, 0, 0);
  });
  return null;
}

export default function Scene({ mouse, isDark, isMobile = false }) {
  const t = isDark ? THEMES.dark : THEMES.light;

  return (
    <Canvas
      camera={{ position: [0, 0, 5.5], fov: 50 }}
      dpr={[1, isMobile ? 1.2 : 2]}
      gl={{ antialias: true, alpha: true }}
      style={{ background: 'transparent' }}
    >
      <ambientLight intensity={t.ambient} />
      <directionalLight position={[5, 5, 5]} intensity={t.directional} color="#ffffff" />
      <pointLight position={[-4, 3, 3]} intensity={0.4} color={t.point1} />
      <pointLight position={[3, -2, 4]} intensity={0.25} color={t.point2} />

      <CameraRig mouse={mouse} />
      <F22Wireframe t={t} />
      <RobotBuddy mouse={mouse} t={t} />
      <Particles count={isMobile ? 70 : 180} mouse={mouse} t={t} />

      <FloatingShape position={[-3.5, 1.5, -3]} type="octahedron" speed={0.3} size={0.5} t={t} />
      <FloatingShape position={[3.8, -1.5, -4]} type="box" speed={0.4} size={0.35} t={t} />
      <FloatingShape position={[-2.5, -1.8, -2.5]} type="tetrahedron" speed={0.35} size={0.4} t={t} />
      {!isMobile && (
        <>
          <FloatingShape position={[4.2, 2.5, -5]} type="icosahedron" speed={0.25} size={0.45} t={t} />
          <FloatingShape position={[-4.5, -0.5, -6]} type="box" speed={0.2} size={0.3} t={t} />
          <FloatingShape position={[0, 3, -4]} type="octahedron" speed={0.15} size={0.25} t={t} />
        </>
      )}

      <GridFloor t={t} />
    </Canvas>
  );
}
